import { authApi } from "../apis/authApi";

export type HaloPermissionCheckStatus = "success" | "failure" | "scope_only";

export type HaloPermissionCheckRow = {
  permission: string;
  ok: boolean;
  status?: HaloPermissionCheckStatus;
  reason: string;
};

export type HaloEndpointCheck = {
  label: string;
  status: number | null;
  error: string;
};

export type HaloPermissionFeedback = {
  success: boolean;
  message: string;
  permissionChecks: HaloPermissionCheckRow[];
  missingScopes: string[];
  failedChecks: HaloEndpointCheck[];
};

export const buildHaloPermissionFeedback = (
  permissionResult: any
): HaloPermissionFeedback => {
  const details = permissionResult?.details || {};
  const missingScopes = Array.isArray(details?.missing_scopes)
    ? details.missing_scopes.map((scope: unknown) => String(scope))
    : [];
  const permissionChecks = Array.isArray(details?.permission_checks)
    ? details.permission_checks
        .filter((p: any) => p && typeof p.permission === "string")
        .map((p: any) => ({
          permission: String(p.permission),
          ok: Boolean(p.ok),
          status:
            p.status === "success" ||
            p.status === "failure" ||
            p.status === "scope_only"
              ? p.status
              : undefined,
          reason: String(p.reason || ""),
        }))
    : [];
  const failedChecks = Array.isArray(details?.endpoint_checks)
    ? details.endpoint_checks
        .filter((c: any) => !c?.ok)
        .map((c: any) => ({
          label: String(c?.label || "endpoint"),
          status: c?.status ?? null,
          error: String(c?.error || "Request failed"),
        }))
    : [];

  return {
    success: Boolean(permissionResult?.success),
    message: String(permissionResult?.message || "Halo permission test completed."),
    permissionChecks,
    missingScopes,
    failedChecks,
  };
};

export const buildHaloPermissionErrorFeedback = (
  error: any
): HaloPermissionFeedback => ({
  success: false,
  message:
    error?.response?.data?.error ||
    error?.message ||
    "Halo permission checks failed.",
  permissionChecks: [],
  missingScopes: [],
  failedChecks: [],
});

export const fetchHaloPermissionFeedback = async (input: {
  url: string;
  client_id: string;
  client_secret: string;
}): Promise<HaloPermissionFeedback> => {
  try {
    const permissionResult = await authApi.testHaloRequiredPermissions(input);
    return buildHaloPermissionFeedback(permissionResult);
  } catch (error: any) {
    return buildHaloPermissionErrorFeedback(error);
  }
};

export const haloPermissionStatusLabel = (
  check: HaloPermissionCheckRow
): "Success" | "Failure" | "Scope Only" => {
  if (check.status === "scope_only") return "Scope Only";
  return check.ok ? "Success" : "Failure";
};
